'use client';

import { useCallback, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { tw } from '@tw';
import { PreviewShape } from '@/lib/consts';
import { CallChrome } from './CallFrame';
import type { MuxPlayerHandle } from './MuxPlayer';
import { muxAnimatedThumbnail, muxBlurPoster } from '@/lib/utils/mux';

const POSTER_WIDTH = 480;
const POSTER_FPS = 12;
const LQIP_WIDTH = 32;
/** The intro slot's shape, so a card with a video is as tall as one without. */
export const COLLAPSED_RATIO = 16 / 10;
const FALLBACK_RATIO = 3 / 4;

const loadPlayer = () => import('./MuxPlayer').then((m) => m.MuxPlayer);

const MuxPlayer = dynamic(loadPlayer, { ssr: false });

export interface IntroVideoProps {
  name: string;
  /** Null is a state of this component, never a branch for the caller: every
      site that used to choose between two components chose differently. */
  playbackId: string | null;
  /** A still of them for the window with no video: the portrait their page
      was built from, drawn as the small round tile of a camera that is off.
      A video always wins; this only fills the empty state. */
  picture?: string | null;
  /** Set on the one preview high enough on the page to be the LCP candidate. */
  priority?: boolean;
}

/**
 * The intro slot, in both of its states: a call window holding a looping poster
 * that swaps in the real player on Play, or the same window with nobody on
 * camera. Neutral `components/media/` rather than the storefront's `_ui/` since
 * 2026-08-31: the main page's creator card plays the same video, and one
 * segment reaching into another's private folder welds two flows together.
 */
export function IntroVideo({ name, playbackId, picture, priority }: IntroVideoProps) {
  const [open, setOpen] = useState(false);
  const [ratio, setRatio] = useState<number | null>(null);
  const warm = useCallback(() => void loadPlayer(), []);
  const measure = useCallback((img: HTMLImageElement | null) => {
    if (img?.naturalWidth && img.naturalHeight) setRatio(img.naturalWidth / img.naturalHeight);
  }, []);
  const started = useRef(false);
  const playing = useRef(false);
  const startOnMount = useCallback((handle: MuxPlayerHandle | null) => {
    if (!handle || started.current) return;
    started.current = true;
    handle.togglePlay();
  }, []);
  const onPlayerState = useCallback((state: { isPlaying: boolean }) => {
    if (state.isPlaying) {
      playing.current = true;
      return;
    }
    if (!playing.current) return;
    playing.current = false;
    started.current = false;
    setOpen(false);
  }, []);

  // Nobody on camera: their own still when we hold one, else their initial —
  // never a stand-in portrait, their name being the one thing we truly know.
  if (!playbackId) {
    return (
      <div
        aria-hidden
        className="sf-clip relative bg-[#0b0b0c]"
        style={{ aspectRatio: COLLAPSED_RATIO }}
      >
        <span className="absolute inset-0 flex items-center justify-center">
          {picture ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={picture}
              alt=""
              decoding="async"
              loading={priority ? 'eager' : 'lazy'}
              className="aspect-square h-[62%] rounded-full object-cover ring-1 ring-white/15"
            />
          ) : (
            <span className="sf-num flex aspect-square h-[46%] items-center justify-center rounded-full bg-white/10 text-[44px] font-extrabold text-white/60">
              {name.trim().charAt(0).toUpperCase()}
            </span>
          )}
        </span>
        <CallChrome name={name} />
      </div>
    );
  }

  const animated = muxAnimatedThumbnail(playbackId, { width: POSTER_WIDTH, fps: POSTER_FPS });
  const lqip = muxBlurPoster(playbackId, { width: LQIP_WIDTH });

  return (
    <div
      className={tw(
        'sf-clip sf-video-fit sf-intro-box relative bg-[var(--sf-accent-deep)]',
        !open && 'mm-btn'
      )}
      style={{ aspectRatio: open ? (ratio ?? FALLBACK_RATIO) : COLLAPSED_RATIO }}
    >
      {/* eslint-disable @next/next/no-img-element */}
      <img
        src={lqip}
        alt=""
        aria-hidden
        ref={measure}
        onLoad={(e) => measure(e.currentTarget)}
        fetchPriority={priority ? 'high' : 'auto'}
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-lg"
      />
      {!open && (
        <img
          src={animated}
          alt=""
          aria-hidden
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          className="sf-intro-loop absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* eslint-enable @next/next/no-img-element */}

      {open && (
        <MuxPlayer
          ref={startOnMount}
          playbackId={playbackId}
          shape={PreviewShape.Portrait}
          rounded={false}
          showPlayOverlay={false}
          className="absolute inset-0"
          onVideoStateChange={onPlayerState}
          priority
        />
      )}

      {!open && <CallChrome name={name} playable />}

      {!open && (
        <button
          type="button"
          /* The whole window is the target, and its name carries the pill's
             visible word (WCAG 2.5.3). */
          aria-label={`Play ${name}'s intro`}
          onClick={() => setOpen(true)}
          onPointerEnter={warm}
          onPointerDown={warm}
          onFocus={warm}
          className="absolute inset-0"
        />
      )}
    </div>
  );
}
